/**
 * apiClient.js — Centralized axios instance.
 *
 * Reason: We have repeatedly hit bugs caused by handlers reading the auth token
 * from the wrong localStorage key (e.g. 'access_token' vs 'token'). This module
 * provides ONE source of truth so any new handler that uses `apiClient` is
 * guaranteed to have a valid Bearer token attached.
 *
 * Usage:
 *   import { apiClient } from '@/services/apiClient'
 *   // or relative: import { apiClient } from '../services/apiClient'
 *
 *   await apiClient.post('/api/v2/mikrotiks/123/sync-radius-now')
 *
 * baseURL is REACT_APP_BACKEND_URL so callers pass paths starting with '/api/...'.
 *
 * NOTE (low risk migration): existing handlers that already work are NOT changed.
 * New code should prefer apiClient. We migrate old call-sites gradually when
 * touching them for other reasons.
 */
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token'; // canonical key used across the app

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: attach Bearer token from localStorage.
apiClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: surface 401 once, leave routing to the app.
let lastUnauthorizedToastAt = 0;
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      const now = Date.now();
      // de-dupe: avoid spamming toast when many parallel calls fail.
      if (now - lastUnauthorizedToastAt > 4000) {
        lastUnauthorizedToastAt = now;
        try {
          toast.error('Sesi habis atau token tidak valid. Silakan login ulang.');
        } catch (_) {
          // toast not available — ignore
        }
      }
    }
    return Promise.reject(error);
  }
);

/** Convenience helpers — return response.data directly. */
export const apiGet    = (url, config)        => apiClient.get(url, config).then(r => r.data);
export const apiPost   = (url, body, config)  => apiClient.post(url, body, config).then(r => r.data);
export const apiPut    = (url, body, config)  => apiClient.put(url, body, config).then(r => r.data);
export const apiPatch  = (url, body, config)  => apiClient.patch(url, body, config).then(r => r.data);
export const apiDelete = (url, config)        => apiClient.delete(url, config).then(r => r.data);

export default apiClient;
